import React from 'react';
import styled, { css } from 'styled-components';
import classNames from 'classnames';
import { darken, spacing } from '../../style';

// constants
import colors from '../../style/colors';
import { Weights } from '../title';
import Spinner from '../spinner';
import { sizes, variants } from './styles';

const DEFAULT_SIZE = 'lg';
const DEFAULT_VARIANT = 'action';

interface ButtonTheme {
  bgColor?: string;
  activeBgColor?: string;
  activeColor?: string;
  borderColor?: string;
  loadingBorderColor?: string;
  hoverBorderColor?: string;
  activeBorderColor?: string;
  hoverBgColor?: string;
  color?: string;
  loaderProps?: { size?: any; variant?: any };
}

const shapes: {
  [key in 'default' | 'pill']: string;
} = {
  default: '2px',
  pill: '9999px',
};
const getBackgroundColor = ({ variant, actionColor }: { variant: keyof typeof variants; actionColor: string }) => {
  const selectedTheme = variants[variant] || {};
  const themeBgColor = selectedTheme['bgColor'];

  if (themeBgColor === 'action') {
    return actionColor;
  }

  return themeBgColor;
};

const getActiveBackground = ({
  variant,
  actionColor,
  actionLight,
  actionColorDark,
}: {
  variant: keyof typeof variants;
  actionColor: string;
  actionLight: string;
  actionColorDark: string;
}) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeActiveBgColor = selectedTheme['activeBgColor'];
  const themeBackgroundColor = selectedTheme['bgColor'];
  const themeBgColor = selectedTheme['bgColor'];

  if (themeActiveBgColor === 'action') {
    return actionColor;
  }

  if (themeActiveBgColor) {
    if (themeActiveBgColor === 'actionLight') {
      return actionLight;
    }

    if (themeActiveBgColor === 'actionColorDark') {
      return actionColorDark;
    }

    return themeActiveBgColor;
  }

  if (themeBgColor === 'action') {
    return actionColor && darken(0.16, actionColor);
  }

  return themeBackgroundColor && darken(0.16, themeBackgroundColor);
};

const getHoverColor = ({
  variant,
  actionColor,
  actionColorDark,
}: {
  variant: keyof typeof variants;
  actionColor: string;
  actionColorDark?: string;
}) => {
  const hoverColor = variants[variant]?.hoverColor || variants[variant]?.color;

  if (hoverColor === 'action') {
    return actionColor;
  }
  if (hoverColor === 'actionColorDark') {
    return actionColorDark;
  }

  return hoverColor;
};

const getHoverBackground = ({
  variant,
  actionColor,
  actionLight,
}: {
  variant: keyof typeof variants;
  actionColor: string;
  actionLight: string;
}) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themHoverBgColor = selectedTheme['hoverBgColor'];
  const themeBgColor = selectedTheme['bgColor'];

  if (themHoverBgColor === 'action') {
    return actionColor;
  }

  if (themHoverBgColor === 'actionLight') {
    return actionLight;
  }

  if (themHoverBgColor) {
    return themHoverBgColor;
  }

  if (themeBgColor === 'action') {
    return themeBgColor && actionColor && darken(0.08, actionColor);
  }

  return themeBgColor && darken(0.08, themeBgColor);
};

const getActiveColor = ({
  variant,
  actionColor,
  actionColorDark,
}: {
  variant: keyof typeof variants;
  actionColor: string;
  actionColorDark: string;
}) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const activeColor = selectedTheme.activeColor;

  if (activeColor === 'action') {
    return actionColor;
  }

  if (activeColor === 'actionColorDark') {
    return actionColorDark;
  }

  return selectedTheme.activeColor;
};

const getBorderColor = ({ variant, actionColor }: { variant: keyof typeof variants; actionColor: string }) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeBorderColor = selectedTheme.borderColor;

  if (themeBorderColor === 'action') {
    return actionColor;
  }

  if (themeBorderColor) {
    return themeBorderColor;
  }

  return getBackgroundColor({ variant, actionColor });
};

const getLoadingBorderColor = ({ variant, actionColor }: { variant: keyof typeof variants; actionColor: string }) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeLoadingBorderColor = selectedTheme.loadingBorderColor;

  if (themeLoadingBorderColor === 'action') {
    return actionColor;
  }

  if (themeLoadingBorderColor) {
    return themeLoadingBorderColor;
  }
};

const getHoverBorderColor = ({
  variant,
  actionColor,
  actionLight,
}: {
  actionLight: string;
  variant: keyof typeof variants;
  actionColor: string;
}) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeHoverBorderColor = selectedTheme.hoverBorderColor;

  if (themeHoverBorderColor === 'actionLight') {
    return actionLight;
  }

  if (themeHoverBorderColor === 'action') {
    return actionColor;
  }

  if (themeHoverBorderColor) {
    return themeHoverBorderColor;
  }

  return getHoverBackground({ variant, actionColor, actionLight });
};

const getActiveBorderColor = ({
  variant,
  actionColor,
  actionLight,
  actionColorDark,
}: {
  variant: keyof typeof variants;
  actionColor: string;
  actionLight: string;
  actionColorDark: string;
}) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeActiveBorderColor = selectedTheme.activeBorderColor;

  if (themeActiveBorderColor === 'action') {
    return actionColor;
  }

  if (themeActiveBorderColor === 'actionLight') {
    return actionLight;
  }

  if (themeActiveBorderColor === 'actionColorDark') {
    return actionColorDark;
  }

  if (themeActiveBorderColor) {
    return themeActiveBorderColor;
  }

  return getActiveBackground({ variant, actionColor, actionLight, actionColorDark });
};

const getColor = ({ variant, actionColor }: { variant: keyof typeof variants; actionColor: string }) => {
  const selectedTheme: ButtonTheme = variants[variant] || {};
  const themeColor = selectedTheme.color;

  if (themeColor === 'action') {
    return actionColor;
  }

  if (themeColor) {
    return themeColor;
  }

  return getBackgroundColor({ variant, actionColor });
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  loading?: boolean;
  children?: React.ReactNode | string;
  className?: string;
  active?: boolean;
  type?: 'button' | 'submit';
  size?: keyof typeof sizes;
  loaderProps?: { loading?: boolean; variant?: any };
  disabled?: boolean;
  'data-name'?: string;
  'data-testid'?: string;
  'data-action'?: string;
  'data-value'?: string;
  'data-id'?: string;
  'aria-label'?: string;
  componentClass?: React.ElementType;
  to?: string;
  weight?: Weights;
  alignItems?: 'center' | 'flex-start' | 'flex-end';
  block?: boolean;
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-around' | 'space-between';
  display?: 'block' | 'flex' | 'inline-flex';
  alignSelf?: 'center' | 'flex-start' | 'flex-end';
  animation?: string;
  flexShrink?: 1 | 0;
  flexGrow?: 1 | 0;
  textAlign?: 'left' | 'right' | 'center';
  height?: string;
  whiteSpace?: string;
  onClick?: React.MouseEventHandler<any>;
  textTransform?: 'capitalize';
  position?: 'relative' | 'absolute' | 'fixed' | 'static';
  style?: React.CSSProperties;
  href?: string;
  target?: string;
  rel?: string;
  containerId?: string;
  gap?: keyof typeof spacing;
  ref?: React.Ref<HTMLButtonElement>;
  cursor?: React.CSSProperties['cursor'];
  shape?: 'default' | 'pill';
  /**
   * Whether the button should be a download button
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLAnchorElement/download
   */
  download?: boolean;
}

export const ButtonComponent = ({
  variant = DEFAULT_VARIANT,
  loading,
  children,
  className,
  active,
  type = 'button',
  size = DEFAULT_SIZE,
  loaderProps,
  disabled,
  'data-name': dataName,
  'data-action': dataAction,
  'data-value': dataValue,
  'data-id': dataId,
  onClick,
  'aria-label': ariaLabel,
  componentClass: Component = 'button',
  to,
  ...rest
}: ButtonProps) => (
  <Component
    type={type}
    className={classNames(className, { active }, { disabled: disabled || loading })}
    onClick={onClick}
    disabled={disabled || loading}
    data-name={dataName}
    data-action={dataAction}
    data-value={dataValue}
    aria-label={ariaLabel}
    aria-disabled={Component !== 'button' ? disabled : undefined}
    to={to}
    data-id={dataId}
    {...rest}
  >
    {loading && (
      <Spinner
        className={'button-loader'}
        variant={'white'}
        {...sizes[size]?.loaderProps}
        {...variants[variant]?.loaderProps}
        {...loaderProps}
      />
    )}

    {children}
  </Component>
);

const Button = styled(ButtonComponent).withConfig({
  shouldForwardProp: prop =>
    ![
      'weight',
      'textTransform',
      'height',
      'weight',
      'block',
      'alignItems',
      'justifyContent',
      'display',
      'animation',
      'whiteSpace',
      'textAlign',
      'flexShrink',
      'flexGrow',
      'gap',
      'shape',
      'alignSelf',
    ].includes(prop),
})<ButtonProps>`
  ${({ variant = DEFAULT_VARIANT, size = DEFAULT_SIZE, ...props }: ButtonProps) => css`
    padding: ${sizes[size]?.padding};
    height: ${props.height || sizes[size].height};

    border-radius: ${shapes[props.shape || 'default']};

    text-transform: ${props.textTransform};
    border: 1px solid ${props => getBorderColor({ variant, actionColor: props.theme.actionColor })};
    background-color: ${props => getBackgroundColor({ variant, actionColor: props.theme.actionColor })};
    transition: all 160ms ease;
    vertical-align: middle;
    touch-action: manipulation;
    cursor: ${props.cursor ?? 'pointer'};
    background-image: none; // Reset unusual Firefox-on-Android default style; see https://github.com/necolas/normalize.css/issues/214
    color: ${props => getColor({ variant, actionColor: props.theme.actionColor })};
    display: ${props.block ? 'flex' : 'inline-flex'};
    align-items: ${props.alignItems || 'center'};
    justify-content: ${props.justifyContent || 'center'};
    align-self: ${props.alignSelf || 'auto'};
    font-size: ${sizes[size].fontSize};
    position: ${props.position || 'relative'};
    gap: ${spacing[props.gap || 'xs']};
    ${props.loading &&
    css`
      & {
        color: transparent !important;
      }

      & > *:not(.button-loader) {
        opacity: 0 !important;
      }
    `};

    ${props.block &&
    css`
      width: 100%;
    `};

    ${props.display === 'block' &&
    css`
      display: block;
    `};

    ${props.animation &&
    css`
      &:hover:not([disabled]):not(.disabled) {
        & > * {
          animation: ${props.animation} 600ms 2;
        }
      }
    `};

    flex-shrink: ${props.flexShrink};

    ${props.flexGrow &&
    css`
      flex-grow: ${props.flexGrow};
    `};

    ${props.whiteSpace &&
    css`
      white-space: ${props.whiteSpace};
    `};

    ${css`
      text-align: ${props.textAlign};
    `};

    &,
    & * {
      font-weight: ${props.weight || 500};
    }

    &,
    &:active,
    &.active {
      &:focus,
      &.focus {
        outline: none;
      }
    }

    &:focus-visible,
    &:hover,
    &:focus,
    &.focus {
      text-decoration: none;
      color: ${variants[variant]?.color};
    }

    &:active,
    &.active {
      outline: 0;
      background-image: none;
      box-shadow: none;
      border-style: solid;
      background-color: ${props =>
        getActiveBackground({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
          actionColorDark: props.theme.actionColorDark,
        })};
      color: ${props =>
        getActiveColor({
          variant,
          actionColor: props.theme.actionColor,
          actionColorDark: props.theme.actionColorDark,
        })};
    }

    &.disabled,
    &[disabled],
    fieldset[disabled] & {
      cursor: not-allowed;
      box-shadow: none;
      background-color: ${variants[variant]?.disabledBackground || colors.gray100};
      border-color: ${themeProps =>
        (props.loading &&
          getLoadingBorderColor({
            variant,
            actionColor: themeProps.theme.actionColor,
          })) ||
        variants[variant]?.disabledBorder ||
        colors.gray100};
      color: ${variants[variant]?.disabledColor || colors.disabled};
      border-style: solid;

      > * {
        color: ${variants[variant]?.disabledColor || colors.disabled};
        cursor: not-allowed;
      }
    }

    &:focus-visible:not(.disabled):not([disabled]) {
      box-shadow: 0 0 0 3px ${colors.sky};
    }

    &:hover:not(.disabled):not([disabled]) {
      border-color: ${props =>
        getHoverBorderColor({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
        })};
      background-color: ${props =>
        getHoverBackground({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
        })};
      border-style: solid;
      color: ${props =>
        getHoverColor({
          variant,
          actionColor: props.theme.actionColor,
          actionColorDark: props.theme.actionColorDark,
        })};
    }

    &.active:not(.disabled):not([disabled]),
    &:active:not(.disabled):not([disabled]) {
      background-color: ${props =>
        getActiveBackground({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
          actionColorDark: props.theme.actionColorDark,
        })};
      border-color: ${props =>
        getActiveBorderColor({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
          actionColorDark: props.theme.actionColorDark,
        })};
      color: ${props =>
        getActiveColor({
          variant,
          actionColor: props.theme.actionColor,
          actionColorDark: props.theme.actionColorDark,
        })};
      border-style: solid;
    }

    &:active:hover:not(.disabled):not([disabled]),
    &.active:hover:not(.disabled):not([disabled]) {
      background-color: ${props =>
        darken(
          0.04,
          getActiveBackground({
            variant,
            actionColor: props.theme.actionColor,
            actionLight: props.theme.actionLight,
            actionColorDark: props.theme.actionColorDark,
          }) || '',
        )};
      border-color: ${props =>
        getActiveBorderColor({
          variant,
          actionColor: props.theme.actionColor,
          actionLight: props.theme.actionLight,
          actionColorDark: props.theme.actionColorDark,
        })};
      color: ${props =>
        getActiveColor({
          variant,
          actionColor: props.theme.actionColor,
          actionColorDark: props.theme.actionColorDark,
        })};
      border-style: solid;
    }

    .button-loader {
      position: absolute;
    }
  `}
`;

Button.displayName = 'Button';
export default Button;
